import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Send, Video, Loader2, MoreVertical, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ReportDialog from '../components/ReportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchChat();
    
    // Poll for new messages every 3 seconds
    pollInterval.current = setInterval(fetchChat, 3000);
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChat = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(data.messages);
      setOtherUser(data.other_user);
    } catch (e) {
      console.error('Failed to fetch chat:', e);
      if (e.response?.status === 404) {
        navigate('/app/matches');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');

    try {
      const { data } = await axios.post(`${API}/chat/send`, {
        match_id: matchId,
        content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, data.message]);
    } catch (e) {
      console.error('Failed to send message:', e);
      setNewMessage(content);
    } finally {
      setSending(false);
    }
  };

  const initiateVideoCall = async () => {
    try {
      const { data } = await axios.post(`${API}/call/initiate/${matchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/app/call/${data.room_id}`);
    } catch (e) {
      console.error('Failed to initiate call:', e);
    }
  };

  const photo = otherUser?.photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=100';

  return (
    <div className="h-screen bg-[#050505] flex flex-col">
      {/* Report Dialog */}
      {showReport && otherUser && (
        <ReportDialog
          userId={otherUser._id}
          userName={otherUser.name}
          onClose={() => setShowReport(false)}
          onBlocked={() => { setShowReport(false); navigate('/app/matches'); }}
        />
      )}

      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-zinc-900 bg-[#050505]">
        <button
          data-testid="back-to-matches"
          onClick={() => navigate('/app/matches')}
          className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {otherUser && (
          <div className="flex items-center gap-3 flex-1">
            <img
              src={photo}
              alt={otherUser.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <h2 className="font-medium text-white truncate">{otherUser.name}</h2>
            </div>
          </div>
        )}

        <Button
          data-testid="video-call-btn"
          onClick={initiateVideoCall}
          variant="ghost"
          size="icon"
          className="text-rose-500 hover:text-rose-400 hover:bg-rose-500/10"
        >
          <Video className="w-5 h-5" />
        </Button>
        <button
          data-testid="chat-report-btn"
          onClick={() => setShowReport(true)}
          className="w-10 h-10 rounded-full flex items-center justify-center text-zinc-400 hover:text-white"
        >
          <MoreVertical className="w-5 h-5" />
        </button>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 text-rose-500 animate-spin" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="text-center py-10">
            <p className="text-zinc-500">
              Say hi to {otherUser?.name}! Start the conversation.
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg._id}
            message={msg}
            isOwn={msg.sender_id === user._id}
          />
        ))}
        
        <div ref={messagesEndRef} />
      </main>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-zinc-900 bg-[#050505]">
        <div className="flex items-center gap-3">
          <Input
            data-testid="chat-input"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12 rounded-full px-5"
          />
          <Button
            data-testid="send-message-btn"
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 rounded-full bg-rose-600 hover:bg-rose-500 p-0"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({ message, isOwn }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div
        data-testid={`message-${message._id}`}
        className={`max-w-[75%] px-4 py-2.5 ${
          isOwn
            ? 'bg-rose-600 text-white rounded-2xl rounded-tr-sm'
            : 'bg-zinc-800 text-white rounded-2xl rounded-tl-sm'
        }`}
      >
        <p className="text-sm">{message.content}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-rose-200' : 'text-zinc-500'}`}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}
